import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  UsePaymentHistoryResult,
  UsePaymentResourcesResult,
  UseSubmitPaymentResult,
} from '@/api/payments';
import {
  PAYMENT_QUICK_AMOUNTS,
  type PaymentProvider,
} from '@/domain/payments';
import { ULTRAPAY_CRYPTO_PAIRS } from '@/domain/payment-providers';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { cn } from '@/lib/cn';
import { Button, Input, Select, Spinner, Toast } from '@/ui/common';
import { PaymentInfoCard } from './PaymentInfoCard';
import { PaymentResult } from './PaymentResult';
import { RecentPaymentRows } from './RecentPaymentRows';
import { SelectedProviderHeader } from './SelectedProviderHeader';

export interface WithdrawalFormPanelProps {
  provider: PaymentProvider;
  currency: string;
  resources: UsePaymentResourcesResult;
  history: UsePaymentHistoryResult;
  submitPayment: UseSubmitPaymentResult;
  className?: string;
}

export function WithdrawalFormPanel({
  provider,
  currency,
  resources,
  history,
  submitPayment,
  className,
}: WithdrawalFormPanelProps) {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const minAmount = provider.MinAmount ?? 0;
  const maxAmount = provider.MaxAmount ?? Number.POSITIVE_INFINITY;
  const [amount, setAmount] = useState(String(minAmount || ''));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  const fieldSet = resources.fields;
  const successMode = resources.config?.success ?? 'notify';

  const cryptoNetworks = useMemo(() => {
    const coin = fields.cryptocurrency ?? '';
    if (!coin) return [];
    return ULTRAPAY_CRYPTO_PAIRS.filter((pair) => pair.Cryptocurrency === coin).map(
      (pair) => pair.Network,
    );
  }, [fields.cryptocurrency]);

  const cryptoCoins = useMemo(
    () => [...new Set(ULTRAPAY_CRYPTO_PAIRS.map((pair) => pair.Cryptocurrency))],
    [],
  );

  const setField = (key: string, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setValidationError(null);
    submitPayment.reset();

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setValidationError(label('Enter a valid amount.', 'Enter a valid amount.'));
      return;
    }
    if (numericAmount < minAmount) {
      setValidationError(
        `${label('Withdrawal minimum', 'Withdrawal minimum')} ${minAmount}`,
      );
      return;
    }
    if (Number.isFinite(maxAmount) && numericAmount > maxAmount) {
      setValidationError(
        `${label('Withdrawal maximum', 'Withdrawal maximum')} ${maxAmount}`,
      );
      return;
    }

    try {
      await submitPayment.submit({
        mode: 'withdrawal',
        provider,
        amount: numericAmount,
        fields,
      });
      history.refetch();
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : label('Payment failed', 'Payment failed'),
      );
    }
  };

  return (
    <div className={cn('flex flex-col gap-4 lg:gap-6', className)}>
      <SelectedProviderHeader
        provider={provider}
        changeTo="/profile/withdrawals"
        selectedLabel={label('Selected provider', 'Selected provider')}
        changeLabel={label('Change provider', 'Change provider')}
      />

      <form onSubmit={onSubmit} className="flex max-w-[560px] flex-col gap-4">
        {resources.paymentAccounts.length > 0 ? (
          <Select
            label={label('Saved account', 'Saved account')}
            options={resources.paymentAccounts.map((account) => ({
              value: String(account.MachineProviderId),
              label: account.AccountNumber,
            }))}
            placeholder={label('Select account', 'Select account')}
            value={fields.machineProviderId ?? ''}
            onChange={(event) => {
              const selected = resources.paymentAccounts.find(
                (account) => String(account.MachineProviderId) === event.target.value,
              );
              setField('machineProviderId', event.target.value);
              if (selected) setField('accountNumber', selected.AccountNumber);
            }}
          />
        ) : null}

        {fieldSet.includes('bank') ? (
          <Select
            label={label('Bank', 'Bank')}
            options={resources.banks.map((bank) => ({
              value: String(bank.Id),
              label: bank.Name,
            }))}
            placeholder={label('Select bank', 'Select bank')}
            value={fields.bank ?? ''}
            onChange={(event) => setField('bank', event.target.value)}
            required={resources.banks.length > 0}
          />
        ) : null}

        {fieldSet.includes('iban') ? (
          <Input
            label={label('IBAN', 'IBAN')}
            value={fields.iban ?? ''}
            onChange={(event) => setField('iban', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('accountHolder') ? (
          <Input
            label={label('Account holder', 'Account holder')}
            value={fields.accountHolder ?? ''}
            onChange={(event) => setField('accountHolder', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('accountNumber') ? (
          <Input
            label={label('Account number', 'Account number')}
            value={fields.accountNumber ?? ''}
            onChange={(event) => setField('accountNumber', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('walletType') && resources.walletTypes.length > 0 ? (
          <Select
            label={label('Wallet type', 'Wallet type')}
            options={resources.walletTypes.map((item) => ({
              value: String(item.Id),
              label: item.Name,
            }))}
            placeholder={label('Select wallet type', 'Select wallet type')}
            value={fields.walletType ?? ''}
            onChange={(event) => setField('walletType', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('cryptocurrency') ? (
          <Select
            label={label('Crypto currency', 'Crypto currency')}
            options={cryptoCoins.map((coin) => ({ value: coin, label: coin }))}
            placeholder={label('Select coin', 'Select coin')}
            value={fields.cryptocurrency ?? ''}
            onChange={(event) => {
              setField('cryptocurrency', event.target.value);
              setField('network', '');
            }}
            required
          />
        ) : null}

        {fieldSet.includes('network') ? (
          <Select
            label={label('Network', 'Network')}
            options={cryptoNetworks.map((network) => ({ value: network, label: network }))}
            placeholder={label('Select network', 'Select network')}
            value={fields.network ?? ''}
            onChange={(event) => setField('network', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('cryptoAddress') ? (
          <Input
            label={label('Crypto address', 'Crypto address')}
            value={fields.cryptoAddress ?? ''}
            onChange={(event) => setField('cryptoAddress', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('phone') ? (
          <Input
            label={label('Phone', 'Phone')}
            value={fields.phone ?? ''}
            onChange={(event) => setField('phone', event.target.value)}
            required
          />
        ) : null}

        {fieldSet.includes('amount') ? (
          <>
            <Input
              label={label('Amount', 'Amount')}
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
            />
            <div className="flex flex-wrap gap-2">
              {PAYMENT_QUICK_AMOUNTS.map((quick) => (
                <Button
                  key={quick}
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const current = Number(amount) || 0;
                    setAmount(String(current + quick));
                  }}
                >
                  {`+${quick}`}
                </Button>
              ))}
            </div>
          </>
        ) : null}

        {validationError ? <Toast tone="danger">{validationError}</Toast> : null}

        <PaymentResult
          response={submitPayment.result}
          successMode={successMode}
          successTitle={label('Withdrawal submitted', 'Withdrawal submitted')}
          errorTitle={label('Withdrawal failed', 'Withdrawal failed')}
          closeLabel={label('Close', 'Close')}
          copyLabel={label('Copy', 'Copy')}
          copiedLabel={label('Copied', 'Copied')}
          onDismiss={() => submitPayment.reset()}
        />

        <Button type="submit" fullWidth loading={submitPayment.isSubmitting}>
          {label('Withdraw', 'Withdraw')}
        </Button>

        <PaymentInfoCard
          provider={provider}
          currency={currency}
          limitsLabel={label('Limits', 'Limits')}
          durationLabel={label('Duration', 'Duration')}
          feeLabel={label('Fee', 'Fee')}
        />
      </form>

      {history.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      ) : (
        <RecentPaymentRows
          rows={history.rows}
          currency={currency}
          title={label('Recent withdrawals', 'Recent withdrawals')}
          emptyLabel={label('No recent withdrawals.', 'No recent withdrawals.')}
        />
      )}
    </div>
  );
}
